import { AtsJobPicker, type AtsJobSelection } from '@/features/ats-job-picker/ats-job-picker'
import { useForm, useStore } from '@tanstack/react-form'
import console from 'console'
import { createStore, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
	getBrandValidationErrorMessage,
	useBrandLibraryStore,
	validateBrandSet,
} from '../../constructs/branding/simplified'
import { brandLibraryAtom, brandLibraryLoadingAtom } from '../../constructs/branding/simplified/state'
import { validationErrorMessages } from '../../rapid-ui'
import { logger } from '../../utilities'
import { ConformedBranding } from '../../views'
import { campaignDetailsAtom, campaignDetailsBrandingAtom } from '../state'
import { BrandingArea } from './branding-area'
import { DetailsArea } from './details-area'
import { KeyImageArea } from './key-image-area'
import { NewModeSubmit } from './new-mode-submit'
import { campaignDetailsValidationSchema, campaignUrlValidationSchema } from './validation'

/**
 * Finds the first valid brand in the brand library
 * A brand is valid if it has at least one complete color palette
 */
function findFirstValidBrand(brandLibrary: ConformedBranding[]): ConformedBranding | null {
	for (const brand of brandLibrary) {
		const validation = validateBrandSet(brand)
		if (validation.isValid) {
			return brand
		}
	}
	return null
}

type GenerateFormValues = {
	brand: string | null
	title: string
	location: string
	language: string
	requirements: string
	url: string
	atsJob: AtsJobSelection | null
	generateKeyImage: boolean
}

export type NewModeDetailsAndBrandingProps = {
	onGenerateFromScan: (url: string, brandId: string, generateKeyImage: boolean) => void
	onGenerateFromAtsJob: (atsJob: AtsJobSelection, brandId: string, generateKeyImage: boolean) => void
	onGenerateFromManual: (
		data: {
			title: string
			location: string
			language: string
			requirements: string
		},
		brandId: string,
		generateKeyImage: boolean,
	) => void
	brandStore?: ReturnType<typeof createStore>
	organisationId: string
	initialValues?: {
		title?: string
		location?: string
		language?: string
		requirements?: string
		url?: string
		mode?: 'scan' | 'manual' | 'ats'
		scrapeError?: string
	}
	serverErrors?: Record<string, string>
}

function isGenerateFormField(field: string): field is keyof GenerateFormValues {
	return ['brand', 'title', 'location', 'language', 'requirements', 'url', 'atsJob', 'generateKeyImage'].includes(
		field,
	)
}

/** The ATS job travels as two request fields; their errors belong to the one picker. */
function formFieldOf(serverField: string): string {
	return serverField === 'atsJobId' || serverField === 'exportIntegrationId' ? 'atsJob' : serverField
}

export function NewModeDetailsAndBranding(
	{
		onGenerateFromScan,
		onGenerateFromAtsJob,
		onGenerateFromManual,
		brandStore,
		organisationId,
		initialValues,
		serverErrors,
	}: NewModeDetailsAndBrandingProps,
) {
	const { t } = useTranslation('common')
	const [mode, setMode] = useState<string>(initialValues?.mode ?? 'scan')
	const [unmappedServerErrors, setUnmappedServerErrors] = useState<Record<string, string>>({})

	const handleModeChange = useCallback((newMode: string) => {
		setMode(newMode)

		// Track when user selects manual entry mode
		if (newMode === 'manual') {
			logger.track('Editor: Manual entry selected', {
				data: {
					organisation_id: organisationId,
				},
			})
		}

		// Track when user selects scan mode
		if (newMode === 'scan') {
			logger.track('Editor: Scan selected', {
				data: {
					organisation_id: organisationId,
				},
			})
		}

		if (newMode === 'ats') {
			logger.track('Editor: ATS job selected', {
				data: {
					organisation_id: organisationId,
				},
			})
		}
	}, [organisationId])

	const [selectedBrand, setSelectedBrand] = useState<ConformedBranding | null>(null)
	const autoSubmitAttempted = useRef(false)

	// Update atoms so simulators can display the selected branding and campaign details
	const setBrandingAtom = useSetAtom(campaignDetailsBrandingAtom)
	const setCampaignDetails = useSetAtom(campaignDetailsAtom)

	// Access brand library state for validation
	const store = useBrandLibraryStore({ overrideStore: brandStore })
	const brandLibrary = useAtomValue(brandLibraryAtom, { store })
	const brandLibraryLoading = useAtomValue(brandLibraryLoadingAtom, { store })

	// Compute validation state
	const brandValidation = useMemo(() => validateBrandSet(selectedBrand), [selectedBrand])
	const errorMessage = useMemo(
		() => getBrandValidationErrorMessage(brandLibrary, selectedBrand, brandLibraryLoading),
		[brandLibrary, selectedBrand, brandLibraryLoading],
	)

	// Determine if form should be disabled
	const isFormDisabled = brandLibraryLoading || !brandValidation.isValid || brandLibrary.length === 0

	const defaultValues: GenerateFormValues = {
		brand: null,
		title: initialValues?.title ?? '',
		location: initialValues?.location ?? '',
		language: initialValues?.language ?? '',
		requirements: initialValues?.requirements ?? '',
		url: initialValues?.url ?? '',
		atsJob: null,
		generateKeyImage: true,
	}

	const { Field, store: formStore, setFieldValue, setFieldMeta, handleSubmit, reset } = useForm({
		defaultValues,
		validators: {
			onSubmitAsync: async ({ value }) => {
				setUnmappedServerErrors({})

				logger.track('generate_campaign_submit_attempted', {
					category: 'campaign_creation',
					data: {
						mode,
						brandId: value.brand,
						hasBrandId: !!value.brand,
						brandIdLength: value.brand?.length ?? 0,
						hasUrl: !!value.url,
						urlLength: value.url?.length ?? 0,
						hasTitle: !!value.title,
					},
				})

				// Build schema dynamically based on current mode
				const schema = z.object({
					brand: z.string().min(1), // fail if no brand is selected
					...(mode === 'manual'
						? campaignDetailsValidationSchema.shape
						: {
							title: z.any(),
							location: z.any(),
							language: z.any(),
							requirements: z.any(),
						}),

					...(mode === 'scan' ? campaignUrlValidationSchema.shape : { url: z.any() }),
					atsJob: mode === 'ats'
						? z.object({ atsJobId: z.string() }, { error: t('editor.details.atsJobs.pickAJob') })
						: z.any(),
				})

				const result = schema.safeParse(value)
				if (!result.success) {
					// Convert Zod's field errors to TanStack Form's expected format
					const fieldErrors: Record<string, string> = {}
					const flattenedErrors = z.flattenError(result.error)
					const zodErrors: Record<string, string[] | undefined> = flattenedErrors.fieldErrors

					for (const [field, errors] of Object.entries(zodErrors)) {
						if (errors && errors.length > 0) {
							fieldErrors[field] = errors[0]
						}
					}

					logger.warn('Generate form validation failed', {
						category: 'campaign_creation',
						data: {
							mode,
							fieldErrors,
							zodErrors: flattenedErrors,
							submittedValues: {
								brandId: value.brand,
								hasBrandId: !!value.brand,
								hasUrl: !!value.url,
								hasTitle: !!value.title,
							},
						},
					})

					return {
						form: undefined,
						fields: fieldErrors,
					}
				}
				return undefined
			},
		},
		onSubmit: (values) => {
			logger.info('Generate form onSubmit callback reached', {
				category: 'campaign_creation',
				data: {
					mode,
					brandId: values.value.brand,
					url: values.value.url,
					hasTitle: !!values.value.title,
				},
			})

			if (!values.value.brand) {
				// this should be prevented by the validator,
				logger.warn('Generate form: No brand selected (should not happen)', {
					category: 'campaign_creation',
				})
				return
			}

			const { generateKeyImage } = values.value
			const trackedKeyImage = generateKeyImage ? 'yes' : 'no'

			if (mode === 'scan') {
				logger.track('generate_campaign_from_scan', {
					category: 'campaign_creation',
					data: { url: values.value.url, brandId: values.value.brand, generate_key_image: trackedKeyImage },
				})
				onGenerateFromScan(values.value.url, values.value.brand, generateKeyImage)
			} else if (mode === 'ats' && values.value.atsJob) {
				logger.track('generate_campaign_from_ats_job', {
					category: 'campaign_creation',
					data: { brandId: values.value.brand, generate_key_image: trackedKeyImage },
				})
				onGenerateFromAtsJob(values.value.atsJob, values.value.brand, generateKeyImage)
			} else {
				logger.track('generate_campaign_from_manual', {
					category: 'campaign_creation',
					data: {
						brandId: values.value.brand,
						hasTitle: !!values.value.title,
						generate_key_image: trackedKeyImage,
					},
				})
				onGenerateFromManual(
					{
						title: values.value.title,
						location: values.value.location,
						language: values.value.language,
						requirements: values.value.requirements,
					},
					values.value.brand,
					generateKeyImage,
				)
			}
		},
	})

	const state = useStore(formStore, (formState) => ({
		values: formState.values,
		isSubmitting: formState.isSubmitting,
		canSubmit: formState.canSubmit,
		fieldMeta: formState.fieldMeta,
	}))

	useEffect(() => {
		if (!serverErrors) {
			return
		}

		const unmapped: Record<string, string> = {}
		for (const [serverField, message] of Object.entries(serverErrors)) {
			const field = formFieldOf(serverField)
			if (isGenerateFormField(field)) {
				setFieldMeta(field, (previousMeta) => ({
					...previousMeta,
					errorMap: { ...previousMeta.errorMap, onSubmit: message },
				}))
			} else {
				unmapped[field] = message
			}
		}
		setUnmappedServerErrors(unmapped)
	}, [serverErrors, setFieldMeta])

	useEffect(() => {
		setFieldValue('brand', selectedBrand?.id ?? null)
		// Sync selected brand to the atom so simulators can display it
		if (selectedBrand) {
			setBrandingAtom(selectedBrand)
			setFieldValue('generateKeyImage', selectedBrand.generateKeyImage)
		}
	}, [selectedBrand, setFieldValue, setBrandingAtom])

	// Sync form values to campaign details atom for brand-aware placeholders
	useEffect(() => {
		if (selectedBrand) {
			setCampaignDetails({
				title: state.values.title,
				location: state.values.location,
				language: state.values.language,
				requirements: state.values.requirements,
				branding: selectedBrand,
				invoiceUrl: undefined,
			})
		}
	}, [
		state.values.title,
		state.values.location,
		state.values.language,
		state.values.requirements,
		selectedBrand,
		setCampaignDetails,
	])

	useEffect(() => {
		// Resets form pristine state when mode changes
		reset({
			brand: state.values.brand,
			title: state.values.title,
			location: state.values.location,
			language: state.values.language,
			requirements: state.values.requirements,
			url: state.values.url,
			atsJob: state.values.atsJob,
			generateKeyImage: state.values.generateKeyImage,
		}, { keepDefaultValues: true })
	}, [mode])

	useEffect(() => {
		if (
			!autoSubmitAttempted.current
			&& !brandLibraryLoading
			&& brandLibrary.length > 0
			&& (initialValues?.url || initialValues?.title)
			&& !initialValues?.scrapeError
			&& !selectedBrand
		) {
			const firstValidBrand = findFirstValidBrand(brandLibrary)

			if (firstValidBrand) {
				autoSubmitAttempted.current = true
				setSelectedBrand(firstValidBrand)

				setTimeout(() => {
					void handleSubmit()
				}, 100)
			} else {
				autoSubmitAttempted.current = true
			}
		}
	}, [brandLibraryLoading, brandLibrary, initialValues, selectedBrand, handleSubmit])

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault()
				void handleSubmit()
			}}
			data-testid="generate-campaign-form"
			data-form-mode={mode}
			data-form-disabled={isFormDisabled}
			data-form-submitting={state.isSubmitting}
			data-form-can-submit={state.canSubmit}
			data-brand-loading={brandLibraryLoading}
			data-brand-count={brandLibrary.length}
			data-brand-selected={!!selectedBrand}
			data-brand-id={state.values.brand ?? 'null'}
		>
			<Field name="brand">
				{({ state: brandState, handleChange, handleBlur }) => (
					<ErrorBoundary fallbackRender={() => <div>{t('common.error')}</div>}>
						<BrandingArea
							branding={selectedBrand}
							onBrandingChange={(brand) => {
								setSelectedBrand(brand)
								// Sync form field immediately to avoid race condition
								handleChange(brand?.id ?? null)
							}}
							brandStore={brandStore}
							enableCustomization={false}
							autoShowCustomizations={false}
						/>
						{validationErrorMessages(brandState.meta.errors).length > 0 && (
							<div className="text-xs text-red-500 mt-1 px-4">
								{validationErrorMessages(brandState.meta.errors).join(', ')}
							</div>
						)}
					</ErrorBoundary>
				)}
			</Field>
			<Field name="generateKeyImage">
				{({ state: keyImageState, handleChange }) => (
					<KeyImageArea
						value={keyImageState.value}
						brandDefault={selectedBrand?.generateKeyImage ?? null}
						onChange={handleChange}
					/>
				)}
			</Field>
			<Field name="title">
				{({ state: titleState, handleChange: titleChange }) => (
					<Field name="location">
						{({ state: locationState, handleChange: locationChange }) => (
							<Field name="language">
								{({ state: languageState, handleChange: languageChange }) => (
									<Field name="requirements">
										{({ state: descriptionState, handleChange: descriptionChange }) => (
											<Field name="url">
												{({ state: urlState, handleChange: urlChange }) => (
													<DetailsArea
														isReady={true}
														mode={mode}
														onModeChange={handleModeChange}
														title={titleState.value}
														onTitleChange={titleChange}
														location={locationState.value}
														onLocationChange={locationChange}
														language={languageState.value}
														onLanguageChange={languageChange}
														description={descriptionState.value}
														onDescriptionChange={descriptionChange}
														url={urlState.value}
														onUrlChange={urlChange}
														titleErrors={titleState.meta.errors}
														locationErrors={locationState.meta.errors}
														languageErrors={languageState.meta.errors}
														descriptionErrors={descriptionState.meta.errors}
														urlErrors={urlState.meta.errors}
														scrapeError={initialValues?.scrapeError}
														atsJobPicker={
															<Field name="atsJob">
																{(
																	{ state: atsJobState, handleChange: atsJobChange },
																) => (
																	<AtsJobPicker
																		organisationId={organisationId}
																		value={atsJobState.value}
																		onChange={atsJobChange}
																		errors={validationErrorMessages(
																			atsJobState.meta.errors,
																		)}
																	/>
																)}
															</Field>
														}
													/>
												)}
											</Field>
										)}
									</Field>
								)}
							</Field>
						)}
					</Field>
				)}
			</Field>
			<NewModeSubmit
				disabled={isFormDisabled}
				disabledReason={brandLibraryLoading
					? t('branding.loadingBrandSets')
					: brandLibrary.length === 0
					? t('branding.noBrandSetsAvailable')
					: errorMessage || t('branding.pleaseSelectValidBrandSet')}
				isSubmitting={state.isSubmitting}
				validationErrors={{
					...unmappedServerErrors,
					...Object.fromEntries(
						Object.entries(state.fieldMeta)
							.map(([field, meta]) => [field, validationErrorMessages(meta?.errors)[0]])
							.filter(([_, message]) => message !== undefined),
					),
				}}
			/>
		</form>
	)
}
